'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import styles from './docs.module.css';

const sections = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    items: [
      {
        id: 'what-is-novamind',
        title: 'What is the NovaMind AI Voice Agent Platform?',
        content: (
          <>
            <p>The NovaMind AI Voice Agent Platform is an AI-powered solution that automates inbound phone calls for your business. Our agents listen, speak, and hold natural conversations — 24/7, without human intervention.</p>
            <p>Whether you run a medical practice, dental office, restaurant, or any other business, the NovaMind AI Voice Agent Platform ensures you never miss a call again.</p>
            <ul>
              <li><strong>Answers 100% of inbound calls</strong> with zero hold time</li>
              <li><strong>Books appointments</strong> and handles scheduling automatically</li>
              <li><strong>Supports multiple languages</strong> including English, Spanish, and Chinese</li>
              <li><strong>Goes live in under 5 minutes</strong> — no technical setup required</li>
            </ul>
          </>
        ),
      },
      {
        id: 'how-it-works',
        title: 'How It Works',
        content: (
          <>
            <p>The NovaMind AI Voice Agent Platform works in three simple steps:</p>
            <div className={styles.steps}>
              <div className={styles.step}>
                <span className={styles.stepNum}>1</span>
                <div>
                  <strong>Connect your phone number</strong>
                  <p>Forward your existing business number to your NovaMind AI Voice Agent, or get a new number through the platform.</p>
                </div>
              </div>
              <div className={styles.step}>
                <span className={styles.stepNum}>2</span>
                <div>
                  <strong>Configure your agent</strong>
                  <p>Choose a voice, set your business hours, add your services, and customize how the agent greets callers.</p>
                </div>
              </div>
              <div className={styles.step}>
                <span className={styles.stepNum}>3</span>
                <div>
                  <strong>Go live</strong>
                  <p>Your AI agent starts answering calls immediately. Monitor performance from your dashboard in real time.</p>
                </div>
              </div>
            </div>
          </>
        ),
      },
      {
        id: 'quick-start',
        title: 'Quick Start Guide',
        content: (
          <>
            <p>Get your AI agent live in under 5 minutes:</p>
            <ul>
              <li><Link href="/register">Create your NovaMind AI account</Link></li>
              <li>From the dashboard, click <strong>Create Agent</strong></li>
              <li>Select your industry (Medical, Dental, Restaurant, or General)</li>
              <li>Enter your business name, hours, and services</li>
              <li>Choose a voice and greeting style</li>
              <li>Add your phone number and activate the agent</li>
            </ul>
            <div className={styles.highlight}>
              Need help getting started? Contact us at <a href={`mailto:${process.env.NEXT_PUBLIC_CONTACT_EMAIL}`}>{process.env.NEXT_PUBLIC_CONTACT_EMAIL}</a> and we'll walk you through setup.
            </div>
          </>
        ),
      },
    ],
  },
  {
    id: 'agent-setup',
    label: 'Setting Up Your Agent',
    items: [
      {
        id: 'creating-agent',
        title: 'Creating Your First Agent',
        content: (
          <>
            <p>From your dashboard, navigate to <strong>Agents → Create Agent</strong>. You'll be prompted to:</p>
            <ul>
              <li>Select your industry type</li>
              <li>Enter your business name and description</li>
              <li>Set your operating hours</li>
              <li>Define the services your agent should handle</li>
            </ul>
            <p>Once created, your agent appears in the Agents list where you can activate, edit, or monitor it at any time.</p>
          </>
        ),
      },
      {
        id: 'voice-personality',
        title: 'Voice & Personality',
        content: (
          <>
            <p>The NovaMind AI Voice Agent Platform offers multiple voice options to match your brand:</p>
            <ul>
              <li>Choose from professional, friendly, or neutral tones</li>
              <li>Set a custom name for your agent (e.g., "Hi, this is Nova at Bright Smiles Dental")</li>
              <li>Adjust speaking pace and style from the agent settings</li>
            </ul>
            <p>Voice settings can be updated at any time from the dashboard without restarting your agent.</p>
          </>
        ),
      },
      {
        id: 'business-hours',
        title: 'Business Hours & Availability',
        content: (
          <>
            <p>Your agent can be configured to behave differently based on the time of day:</p>
            <ul>
              <li><strong>During business hours:</strong> Full interaction mode — books appointments, answers questions</li>
              <li><strong>After hours:</strong> Inform callers of your hours and offer to take a message or schedule a callback</li>
              <li><strong>Holidays:</strong> Set custom closures with a special greeting</li>
            </ul>
            <p>Business hours are set per agent and can be updated anytime from the Agent Settings panel.</p>
          </>
        ),
      },
      {
        id: 'phone-number',
        title: 'Your Phone Number & Call Forwarding',
        content: (
          <>
            <p>Every NovaMind AI plan includes a dedicated phone number linked to your AI voice agent. You have two options for how to use it:</p>

            <div className={styles.steps}>
              <div className={styles.step}>
                <span className={styles.stepNum}>1</span>
                <div>
                  <strong>Use your new number directly (Recommended)</strong>
                  <p>The simplest setup. Share your new AI agent number with your customers — on your website, Google listing, business cards, etc. Your AI agent answers all calls automatically with no additional configuration.</p>
                </div>
              </div>
              <div className={styles.step}>
                <span className={styles.stepNum}>2</span>
                <div>
                  <strong>Keep your existing business number</strong>
                  <p>If you want to keep your current phone number, you can forward calls from it to your new AI agent number. This is entirely your choice and your responsibility to set up with your phone carrier. NovaMind AI does not manage this forwarding for you.</p>
                </div>
              </div>
            </div>

            <h3>How to Forward Your Existing Number</h3>
            <p>Call forwarding setup depends on your phone type and carrier. Here are the most common methods:</p>

            <h4>Landline — Forward All Calls</h4>
            <ul>
              <li>Pick up your phone and dial <strong>*72</strong></li>
              <li>Dial your AI agent number (found in your dashboard under My Agents)</li>
              <li>Wait for confirmation tone and hang up</li>
              <li>To disable: dial <strong>*73</strong></li>
            </ul>

            <h4>Cell Phone — Forward All Calls</h4>
            <ul>
              <li><strong>iPhone:</strong> Settings → Phone → Call Forwarding → ON → enter AI agent number</li>
              <li><strong>Android:</strong> Phone app → Settings → Call Forwarding → Always Forward → enter AI agent number</li>
              <li><strong>Dial code (all phones):</strong> Dial <strong>*21*[your AI number]#</strong> then press call</li>
              <li>To disable: dial <strong>##21#</strong></li>
            </ul>

            <h4>Forward Only When Busy or No Answer</h4>
            <ul>
              <li><strong>When busy:</strong> Dial <strong>*67*[your AI number]#</strong> (disable: ##67#)</li>
              <li><strong>When no answer:</strong> Dial <strong>*61*[your AI number]#</strong> (disable: ##61#)</li>
              <li><strong>When unreachable:</strong> Dial <strong>*62*[your AI number]#</strong> (disable: ##62#)</li>
            </ul>

            <div className={styles.highlight}>
              <strong>Your AI agent number</strong> is found in your dashboard under <strong>My Agents</strong>. Use this number as the forwarding destination.
            </div>

            <h4>Recommended Setup by Business Type</h4>
            <ul>
              <li><strong>Restaurant (open set hours):</strong> Forward when busy + forward when no answer during hours. Forward all calls after closing.</li>
              <li><strong>Dental / Medical (9am–5pm):</strong> Enable full forwarding at closing time, disable when opening each morning.</li>
              <li><strong>24/7 Operations:</strong> One-time unconditional forward — AI handles everything.</li>
            </ul>

            <h4>Troubleshooting</h4>
            <ul>
              <li>If forwarding codes don't work, contact your carrier to enable the call forwarding feature on your plan</li>
              <li>If calls go to voicemail instead of AI, disable your carrier voicemail first: dial <strong>##004#</strong></li>
              <li>Verify your AI agent is active in the dashboard before testing</li>
            </ul>
          </>
        ),
      },
      {
        id: 'multilanguage',
        title: 'Multi-Language Support',
        content: (
          <>
            <p>The NovaMind AI Voice Agent Platform can detect and respond in the caller's preferred language. Supported languages include:</p>
            <ul>
              <li>English</li>
              <li>Spanish</li>
              <li>Chinese (Mandarin)</li>
            </ul>
            <p>Multi-language support is available on Professional and Enterprise plans. Enable it from the Agent Settings panel.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'industries',
    label: 'Industry Guides',
    items: [
      {
        id: 'medical',
        title: 'Medical Practices',
        content: (
          <>
            <p>The NovaMind AI Voice Agent Platform is purpose-built for medical offices. Your agent can handle:</p>
            <ul>
              <li>Patient appointment scheduling and reminders</li>
              <li>Patient identity verification</li>
              <li>Prescription status inquiries</li>
              <li>Insurance coverage questions</li>
              <li>Routing urgent calls to on-call staff</li>
            </ul>
            <div className={styles.highlight}>
              Medical deployments must comply with HIPAA. See our <Link href="/security">Security page</Link> for details on our HIPAA-aligned infrastructure.
            </div>
            <p>The NovaMind AI Voice Agent Platform integrates with <strong>Epic</strong> and other leading EHR systems. Contact us to configure your integration.</p>
          </>
        ),
      },
      {
        id: 'dental',
        title: 'Dental Practices',
        content: (
          <>
            <p>Dental-specific features include:</p>
            <ul>
              <li>New patient intake and appointment booking</li>
              <li>Insurance verification and coverage questions</li>
              <li>Recall reminders for cleanings and checkups</li>
              <li>After-hours emergency triage</li>
            </ul>
            <p>Supported practice management integrations: <strong>NexHealth, Dentrix, Open Dental, Eaglesoft, Curve Dental</strong>, and 10+ more.</p>
          </>
        ),
      },
      {
        id: 'restaurant',
        title: 'Restaurants',
        content: (
          <>
            <p>Keep your kitchen focused while your agent handles the phones:</p>
            <ul>
              <li>Take orders and confirm reservations</li>
              <li>Answer menu and allergy questions</li>
              <li>Provide hours, location, and parking info</li>
              <li>Handle high call volume during peak hours</li>
            </ul>
            <p>Restaurant agents can be updated with daily specials and menu changes directly from the dashboard.</p>
          </>
        ),
      },
      {
        id: 'general',
        title: 'General Business',
        content: (
          <>
            <p>For any business that receives inbound calls, the General agent handles:</p>
            <ul>
              <li>Customer FAQs and business information</li>
              <li>Appointment and callback scheduling</li>
              <li>Call routing to the right department</li>
              <li>After-hours messaging</li>
            </ul>
            <p>The General agent is fully customizable — define your own topics, responses, and escalation paths.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'dashboard',
    label: 'Dashboard & Analytics',
    items: [
      {
        id: 'dashboard-overview',
        title: 'Dashboard Overview',
        content: (
          <>
            <p>Your NovaMind AI Voice Agent dashboard gives you a real-time view of your agent performance:</p>
            <ul>
              <li><strong>Active Calls:</strong> See how many calls your agent is handling right now</li>
              <li><strong>Total Calls:</strong> Historical call volume over any time period</li>
              <li><strong>Average Duration:</strong> Average length of agent interactions</li>
              <li><strong>Minutes Used:</strong> Track usage against your plan limit</li>
            </ul>
          </>
        ),
      },
      {
        id: 'billing',
        title: 'Billing & Plans',
        content: (
          <>
            <p>The NovaMind AI Voice Agent Platform offers three plans:</p>
            <ul>
              <li><strong>Starter ($99/mo):</strong> 600 minutes, 3 concurrent calls, 1 phone number</li>
              <li><strong>Professional ($299/mo):</strong> 2,000 minutes, 10 concurrent calls, 3 phone numbers — includes HIPAA / BAA</li>
              <li><strong>Enterprise ($699/mo):</strong> 6,500 minutes, 30 concurrent calls, 10 phone numbers — includes HIPAA / BAA</li>
            </ul>
            <p>Manage your plan, update payment info, and view invoices from the <strong>Billing</strong> section of your dashboard.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      {
        id: 'faq',
        title: 'Frequently Asked Questions',
        content: (
          <>
            <ul>
              <li><strong>Can I keep my existing phone number?</strong> Yes — simply forward your number to your NovaMind AI Voice Agent number.</li>
              <li><strong>What happens when my minutes run out?</strong> We'll notify you before you hit your limit. Calls will continue on overage pricing until you upgrade.</li>
              <li><strong>Can I update my agent without downtime?</strong> Any update — from business hours to a full agent rebuild — deploys in under 1 minute, as long as your changes are ready to go. We recommend scheduling updates during off-peak hours to minimize customer impact.</li>
              <li><strong>Is there a free trial?</strong> Yes, we offer a 14-day free trial. Start free, cancel anytime.</li>
            </ul>
          </>
        ),
      },
      {
        id: 'contact-support',
        title: 'Contact Support',
        content: (
          <>
            <p>We're here to help. Reach us at:</p>
            <ul>
              <li>Email: <a href={`mailto:${process.env.NEXT_PUBLIC_CONTACT_EMAIL}`}>{process.env.NEXT_PUBLIC_CONTACT_EMAIL}</a></li>
              <li>Response time: within 1 business day (Professional & Enterprise: priority response)</li>
            </ul>
            <div className={styles.highlight}>
              Enterprise customers have access to a dedicated account manager. Contact us to learn more.
            </div>
          </>
        ),
      },
    ],
  },
];

export default function DocsPage() {
  const allItems = sections.flatMap(s => s.items);
  const [activeId, setActiveId] = useState(allItems[0].id);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && allItems.find(i => i.id === hash)) {
      setActiveId(hash);
    }
  }, []);
  const activeItem = allItems.find(i => i.id === activeId)!;

  return (
    <div className={styles.page}>

      {/* Top Bar */}
      <div className={styles.topBar}>
        <Link href="/" className={styles.backLink}>← Back to Home</Link>
        <Link href="/" className={styles.logoLink}>NovaMind AI</Link>
      </div>

      <div className={styles.layout}>

        {/* Sidebar */}
        <aside className={styles.sidebar}>
          <Link href="/" className={styles.sidebarLogo}>
            <Image
              src="/NovaMind-AI.png"
              alt="NovaMind AI"
              width={120}
              height={48}
              className={styles.sidebarLogoImg}
            />
          </Link>
          <p className={styles.sidebarHeading}>Documentation</p>
          {sections.map(section => (
            <div key={section.id} className={styles.sidebarGroup}>
              <p className={styles.sidebarGroupLabel}>{section.label}</p>
              {section.items.map(item => (
                <button
                  key={item.id}
                  className={`${styles.sidebarItem} ${activeId === item.id ? styles.sidebarItemActive : ''}`}
                  onClick={() => setActiveId(item.id)}
                >
                  {item.title}
                </button>
              ))}
            </div>
          ))}
        </aside>

        {/* Main Content */}
        <main className={styles.main}>
          <div className={styles.card}>
            <h1 className={styles.title}>{activeItem.title}</h1>
            <div className={styles.content}>
              {activeItem.content}
            </div>
          </div>

          {/* Prev / Next Navigation */}
          <div className={styles.pagination}>
            {allItems.findIndex(i => i.id === activeId) > 0 && (
              <button
                className={styles.pageBtn}
                onClick={() => setActiveId(allItems[allItems.findIndex(i => i.id === activeId) - 1].id)}
              >
                ← {allItems[allItems.findIndex(i => i.id === activeId) - 1].title}
              </button>
            )}
            {allItems.findIndex(i => i.id === activeId) < allItems.length - 1 && (
              <button
                className={`${styles.pageBtn} ${styles.pageBtnNext}`}
                onClick={() => setActiveId(allItems[allItems.findIndex(i => i.id === activeId) + 1].id)}
              >
                {allItems[allItems.findIndex(i => i.id === activeId) + 1].title} →
              </button>
            )}
          </div>
        </main>

      </div>

      {/* Footer */}
      <div className={styles.pageFooter}>
        <p>
          © {new Date().getFullYear()} NovaMind AI Inc. All rights reserved.{' '}
          <Link href="/privacy" className={styles.footerLink}>Privacy Policy</Link>
          {' · '}
          <Link href="/terms" className={styles.footerLink}>Terms of Service</Link>
          {' · '}
          <Link href="/security" className={styles.footerLink}>Security</Link>
          {' · '}
          <Link href="/" className={styles.footerLink}>Back to Home</Link>
        </p>
      </div>

    </div>
  );
}
